import React from 'react';
import { Link } from 'react-router-dom';
import { Lock, Sparkles, ArrowRight, Check } from 'lucide-react';
import { Button } from './ui/button';
import { useAuth } from '../contexts/AuthContext';
import { PRO_BUNDLE_NAME } from '../config/proFeatures';

/**
 * Strict paywall — free users cannot see any of the wrapped content.
 *
 * Usage:
 *   <ProGate feature="Screener">
 *     <Screener />
 *   </ProGate>
 *
 * Props:
 *   feature        Display name shown on the paywall (e.g. "Screener").
 *   description    Optional one-liner describing the gated feature.
 *   bullets        Optional bullet list of what they unlock.
 */
const DEFAULT_BULLETS = [
  'Unlimited AI courses + generations',
  'Advanced screener with 30+ filters',
  'Full strategy backtesting + marketplace',
  'Pro-only alerts, journal & research',
  'Member-only Discord + Telegram community',
];

const ProGate = ({ feature, description, bullets = DEFAULT_BULLETS, children }) => {
  const { user, isPro, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 pt-24 flex items-center justify-center text-gray-500">
        Loading…
      </div>
    );
  }

  if (isPro) return children;

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#07070d] via-[#0a0a14] to-[#0d0d1f] pt-24 pb-16 text-white">
      <div className="max-w-3xl mx-auto px-4">
        <div
          className="relative rounded-3xl overflow-hidden border border-white/10 bg-[#0a0a14]/80 backdrop-blur-md p-8 md:p-12 shadow-[0_25px_80px_-30px_rgba(99,102,241,0.55)]"
          data-testid={`pro-gate-${(feature || 'page').toLowerCase().replace(/\s+/g, '-')}`}
        >
          <div
            aria-hidden
            className="absolute -top-32 -right-32 w-80 h-80 rounded-full opacity-40 pointer-events-none"
            style={{
              background:
                'radial-gradient(circle, rgba(34,211,238,0.4) 0%, rgba(139,92,246,0.18) 50%, transparent 70%)',
            }}
          />

          <div className="relative">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white/10 border border-white/15 text-[11px] uppercase tracking-[0.18em] text-cyan-300">
              <Lock className="w-3 h-3" />
              Pro feature
            </div>

            <h1 className="mt-5 text-3xl md:text-4xl font-bold tracking-tight">
              {feature || 'This page'} is a{' '}
              <span className="bg-gradient-to-r from-cyan-300 to-violet-300 bg-clip-text text-transparent">
                Pro
              </span>{' '}
              feature.
            </h1>
            <p className="mt-3 text-white/70 text-sm md:text-base max-w-xl">
              {description ||
                'Unlock the full Neon Alpha investing-education suite — research, screening, journaling, alerts and the private community.'}
            </p>

            <ul className="mt-6 space-y-2.5 text-sm">
              {bullets.map((b) => (
                <li key={b} className="flex items-start gap-2 text-white/80">
                  <Check className="w-4 h-4 text-emerald-400 flex-shrink-0 mt-0.5" />
                  <span>{b}</span>
                </li>
              ))}
            </ul>

            <div className="mt-8 flex flex-col sm:flex-row gap-3">
              <Link to="/pro-access">
                <Button
                  className="bg-gradient-to-r from-cyan-500 to-violet-500 hover:from-cyan-400 hover:to-violet-400 text-white font-semibold shadow-[0_0_25px_rgba(139,92,246,0.5)]"
                  data-testid="pro-gate-upgrade-btn"
                >
                  <Sparkles className="w-4 h-4 mr-1.5" />
                  Get Pro Access
                  <ArrowRight className="w-4 h-4 ml-1.5" />
                </Button>
              </Link>
              {!user && (
                <Link to="/auth">
                  <Button
                    variant="outline"
                    className="border-white/20 text-white hover:bg-white/10 bg-transparent"
                    data-testid="pro-gate-signin-btn"
                  >
                    Sign in
                  </Button>
                </Link>
              )}
            </div>

            <p className="mt-6 text-[11px] text-white/40">
              Educational platform · Not a brokerage · Not investment advice · Cancel anytime
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProGate;
